import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardSleeveDetail } from '../../services/CommonService';
import { getRecipientList } from '../../services/Recipients';
import './Dashboard.css';
import Pagination from 'react-js-pagination';
import Utils from '../../utils';
import '../../assets/ManageSleeve.css';
import { encryptData } from '../../utils/crypto';
import { SALT } from '../../configs/AppConfig';

const Dashboard = () => {
  const navigate = useNavigate();
  const [sleeveDetail, setSleeveDetail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Pagination
  const lengths = Utils.tableShowLengths();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(lengths[0]);

  // Function to render case status badge
  const renderCaseStatusBadge = (caseStatus) => {
    let statusText = 'N/A';
    let statusClass = 'case-status-badge';

    switch (caseStatus?.toLowerCase()) {
      case 'not started':
      case 'notstarted':
        statusText = 'Not Started';
        statusClass += ' case-status-notstarted';
        break;
      case 'in progress':
      case 'inprogress':
        statusText = 'In Progress';
        statusClass += ' case-status-inprogress';
        break;
      case 'submitted':
        statusText = 'Submitted';
        statusClass += ' case-status-submitted';
        break;
      case 'done':
        statusText = 'Done';
        statusClass += ' case-status-done';
        break;
      default:
        statusClass += ' case-status-default';
        break;
    }

    return (
      <span className={statusClass}>
        {statusText}
      </span>
    );
  };

  // Function to render payment status badge
  const renderPaymentStatusBadge = (paymentStatus) => {
    let statusText = 'N/A';
    let statusClass = 'payment-status-badge';

    switch (paymentStatus?.toLowerCase()) {
      case 'unpaid':
        statusText = 'Unpaid';
        statusClass += ' payment-status-unpaid';
        break;
      case 'partial':
        statusText = 'Partial';
        statusClass += ' payment-status-partial';
        break;
      case 'paid':
        statusText = 'Paid';
        statusClass += ' payment-status-paid';
        break;
      default:
        statusClass += ' payment-status-default';
        break;
    }

    return (
      <span className={statusClass}>
        {statusText}
      </span>
    );
  };

  useEffect(() => {
    setLoading(true);

    // Fetch dashboard sleeve detail
    getDashboardSleeveDetail({ status_id: 1, recipient_id: 1 }).then((res) => {
      setSleeveDetail(res.data);
    });

    // Fetch recipients list with higher limit to allow client-side pagination beyond 10
    getRecipientList({
      limit: 1000,
      page: 1,
      search: ''
    }).then((res) => {
      if (res.data && res.data.data) {
        setRecipients(res.data.data || []);
      }
      setLoading(false);
    }).catch((error) => {
      console.error('Error fetching recipients:', error);
      setLoading(false);
    });

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleManageClick = (client) => {
    const encryptedId = encodeURIComponent(encryptData(client.id, SALT));
    navigate(`/client-portal/manage-client/${encryptedId}`);
  };

  const filteredRecipients = useMemo(() => {
    if (!searchTerm) return recipients;
    const term = searchTerm.toLowerCase();
    return (recipients || []).filter((r) => {
      const name = `${r?.first_name || ''} ${r?.last_name || ''}`.toLowerCase();
      return (
        name.includes(term) ||
        (r?.email || '').toLowerCase().includes(term) ||
        (r?.mobile || '').toString().toLowerCase().includes(term)
      );
    });
  }, [recipients, searchTerm]);

  // Reset to first page on search change
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const totalData = filteredRecipients.length;
  const startIndex = Math.min((page - 1) * limit, Math.max(0, totalData - 1));
  const endIndex = Math.min(startIndex + limit, totalData);
  const pagedRecipients = filteredRecipients.slice(startIndex, endIndex);

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  return (
    <div className='dashboard-container'>
      <div className='custom_container'>
        {/* Recipients Section */}
        <div className='recipients-section'>
          <div className='recipients-header-home'>
            <div>
              <h2 className='recipients-dashboard-title'>Recipients Dashboard</h2>
              <p className='recipients-dashboard-subtitle'>Manage and monitor your recipients</p>
            </div>
            <div className='header-actions'>
              <div className='search-filter-controls'>
                <div className='modern-search-container'>
                  <input
                    type="search"
                    placeholder="Search Recipients..."
                    className='modern-search-input'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className='modern-search-icon' aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </span>
                </div>

              </div>
            </div>
          </div>

          {filteredRecipients.length > 0 ? (
            <div className='recipients-list'>
              <div className='table-responsive'>
                <table className='table recipients-table border rounded'>
                  <thead className='table-header'>
                    <tr>
                      <th className='table-header-cell'>S.NO.</th>
                      <th className='table-header-cell'>RECIPIENT NAME</th>
                      <th className='table-header-cell'>EMAIL</th>
                      <th className='table-header-cell'>PHONE</th>
                      <th className='table-header-cell'>PAYMENT STATUS</th>
                      <th className='table-header-cell'>CASE STATUS</th>
                      <th className='table-header-cell'>CREATED DATE</th>
                      <th className='table-header-cell table-header-actions'>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRecipients.map((recipient, index) => (
                      <tr key={recipient.id || index} className='table-row'>
                        <td className='table-cell table-cell-sno'>
                          {startIndex + index + 1}
                        </td>
                        <td className='table-cell'>
                          <div className='client-info'>
                            <div>
                              <div className='client-name'>
                                {`${recipient.first_name || ''} ${recipient.last_name || ''}`.trim() || '-'}

                              </div>
                            </div>
                          </div>
                        </td>
                        <td className='table-cell table-cell-text'>
                          {recipient.email}
                        </td>
                        <td className='table-cell table-cell-text'>
                          {recipient.mobile ? `+${recipient.country_code || ''} ${recipient.mobile}` : '-'}
                        </td>
                        <td className='table-cell'>
                          {renderPaymentStatusBadge(recipient.payment_status)}
                        </td>
                        <td className='table-cell'>
                          {renderCaseStatusBadge(recipient.case_status)}
                        </td>
                        <td className='table-cell table-cell-date'>
                          {recipient.created_at || '-'}
                        </td>
                        <td className='table-cell table-cell-actions'>
                          <div className='action-buttons'>
                            <button
                              className='btn btn-primary'
                              title="Manage Client"
                              onClick={() => handleManageClick(recipient)}
                            >
                              Manage
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalData > limit && (
                <div className="table_footer_wrap">
                  <div className="d-flex align-items-center mb-4">
                    <span className="me-2 text-dark">Show</span>
                    <select
                      className="form-select"
                      value={limit}
                      style={{ width: '80px' }}
                      onChange={handleLimitChange}
                      aria-label="Rows per page"
                    >
                      {lengths && lengths.map((len, i) => (
                        <option value={len} key={i}>{len}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pagination mb-4">
                    <Pagination
                      activePage={page}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalData}
                      pageRangeDisplayed={5}
                      onChange={(e) => setPage(e)}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className='no-data'>
              <p>No data found</p>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default Dashboard;